'use client';

import React, { useState } from 'react';
import { Box, Typography, Container } from '@mui/material';
import { styled } from '@mui/material/styles';
import { color, spacing, letterSpacing, fontWeight, breakpoints } from '@/theme/tokens';
import { useTranslations } from 'next-intl';
import { TabType, usOfficesData } from './Location/types';
import LocationTabs from './Location/LocationTabs';
import { useLocationCarousel } from './Location/useLocationCarousel';
import { useIsGhana } from '@/components/hooks/useIsGhana';

import HomepageMapBrokersDesktopSVG from '@/assets/images/homepage-map-brokers-desktop.svg?react';
import HomepageMapBrokersMobileHorizontalSVG from '@/assets/images/homepage-map-brokers-mobile-horizontal.svg?react';
import HomepageMapBrokersMobileVerticalSVG from '@/assets/images/homepage-map-brokers-mobile-vertical.svg?react';

import HomepageMapOfficesDesktopSVG from '@/assets/images/homepage-map-offices-desktop.svg?react';
import HomepageMapOfficesMobileHorizontalSVG from '@/assets/images/homepage-map-offices-mobile-horizontal.svg?react';
import HomepageMapOfficesMobileVerticalSVG from '@/assets/images/homepage-map-offices-mobile-vertical.svg?react';

import HomepageMapBrokersDesktopGhanaSVG from '@/assets/images/ghana/homepage-map-brokers-desktop-ghana.svg?react';
import HomepageMapBrokersMobileHorizontalGhanaSVG from '@/assets/images/ghana/homepage-map-brokers-mobile-horizontal-ghana.svg?react';
import HomepageMapBrokersMobileVerticalGhanaSVG from '@/assets/images/ghana/homepage-map-brokers-mobile-vertical-ghana.svg?react';

import HomepageMapOfficesDesktopGhanaSVG from '@/assets/images/ghana/homepage-map-offices-desktop-ghana.svg?react';
import HomepageMapOfficesMobileHorizontalGhanaSVG from '@/assets/images/ghana/homepage-map-offices-mobile-horizontal-ghana.svg?react';
import HomepageMapOfficesMobileVerticalGhanaSVG from '@/assets/images/ghana/homepage-map-offices-mobile-vertical-ghana.svg?react';

import USOfficeLocations from './Location/USOfficeLocations';
import AuthorizedBrokers from '../authorizedBrokers/AuthorizedBrokers';
import contactsData from '@/content/contacts/en/contacts-data.json';
import { AuthorizedBrokersColorsPalette } from '../authorizedBrokers/types';
import { countryGroups } from '../authorizedBrokers/countryGroups';

const MOBILE_HORIZONTAL_MIN = '501px';

const SVG_VIEWBOX = {
    desktop: '0 0 1312 600',
    mobileHorizontal: '0 0 501 320',
    mobileVertical: '0 0 360 440',
};

const TabPanel = styled(Box)`
    @keyframes tabFadeIn {
        from { opacity: 0; transform: translateY(${spacing[8]}); }
        to { opacity: 1; transform: translateY(0); }
    }
    animation: tabFadeIn 300ms ease-out both;
`;

const MapContainer = styled(Box)`
    width: 100%;
    overflow: hidden;

    @media (min-width: ${breakpoints.sm}) {
        display: flex;
        justify-content: center;
    }
`;

// Desktop & Tablet (≥768px)
const DesktopTabletMapWrapper = styled(Box)`
    display: none;

    @media (min-width: calc(${breakpoints.sm} + 1px)) {
        display: flex;
        justify-content: center;
        width: 100%;
    }
`;

// Mobile horizontal (501–767px)
const MobileHorizontalMapWrapper = styled(Box)`
    display: none;

    @media (min-width: ${MOBILE_HORIZONTAL_MIN}) {
        display: block;
        width: 100%;
    }

    @media (min-width: calc(${breakpoints.sm} + 1px)) {
        display: none;
    }
`;

// Mobile vertical (0–500px)
const MobileVerticalMapWrapper = styled(Box)`
    display: block;
    width: 100%;

    @media (min-width: ${MOBILE_HORIZONTAL_MIN}) {
        display: none;
    }
`;

// Desktop/Tablet SVG
const desktopSvgStyles = `
    width: 100%;
    height: auto;
    display: block;

    @media (min-width: ${breakpoints.sm}) and (max-width: ${breakpoints.md}) {
        max-width: 1135px;
        min-width: 860px;
        margin: 0 auto;
    }

    @media (min-width: ${breakpoints.md}) {
        max-width: 1135px;
    }

    @media (min-width: ${breakpoints.lg}) {
        max-width: 1312px;
    }
`;

const mobileSvgStyles = `
    width: 100%;
    height: auto;
    display: block;
`;

const HomepageMapOfficesDesktop = styled(HomepageMapOfficesDesktopSVG)`${desktopSvgStyles}`;
const HomepageMapBrokersDesktop = styled(HomepageMapBrokersDesktopSVG)`${desktopSvgStyles}`;
const HomepageMapOfficesMobileHorizontal = styled(HomepageMapOfficesMobileHorizontalSVG)`${mobileSvgStyles}`;
const HomepageMapBrokersMobileHorizontal = styled(HomepageMapBrokersMobileHorizontalSVG)`${mobileSvgStyles}`;
const HomepageMapOfficesMobileVertical = styled(HomepageMapOfficesMobileVerticalSVG)`${mobileSvgStyles}`;
const HomepageMapBrokersMobileVertical = styled(HomepageMapBrokersMobileVerticalSVG)`${mobileSvgStyles}`;

const HomepageMapOfficesDesktopGhana = styled(HomepageMapOfficesDesktopGhanaSVG)`${desktopSvgStyles}`;
const HomepageMapBrokersDesktopGhana = styled(HomepageMapBrokersDesktopGhanaSVG)`${desktopSvgStyles}`;
const HomepageMapOfficesMobileHorizontalGhana = styled(HomepageMapOfficesMobileHorizontalGhanaSVG)`${mobileSvgStyles}`;
const HomepageMapBrokersMobileHorizontalGhana = styled(HomepageMapBrokersMobileHorizontalGhanaSVG)`${mobileSvgStyles}`;
const HomepageMapOfficesMobileVerticalGhana = styled(HomepageMapOfficesMobileVerticalGhanaSVG)`${mobileSvgStyles}`;
const HomepageMapBrokersMobileVerticalGhana = styled(HomepageMapBrokersMobileVerticalGhanaSVG)`${mobileSvgStyles}`;

const colorsPalette: AuthorizedBrokersColorsPalette = {
    accordionBackgroundColor: color.gray[40],
    accordionExpandedBackgroundColor: color.white[0],
    accordionTextColor: color.white[0],
    accordionExpandedTextColor: color.gray[80],
    iconColor: color.white[0],
    iconExpandedColor: color.gray[70],
};

export default function Location() {
    const isGhana = useIsGhana();
    const t = useTranslations('HomePage');
    const [activeTab, setActiveTab] = useState<TabType>('us-offices');

    const carousel = useLocationCarousel(usOfficesData);
    const isOfficesTab = activeTab === 'us-offices';
    const isBrokersTab = activeTab === 'brokers';

    return (
        <Box
            sx={{
                backgroundColor: color.gray[70],
            }}
        >
            <Container sx={{
                paddingTop: { xs: spacing[32], sm: spacing[48], md: spacing[64] },
                paddingX: { xs: 0, sm: 0, md: 0 },
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}>
                <Typography
                    variant="heading5bold"
                    color={color.white[0]}
                    textAlign="center"
                    component="h2"
                    sx={{
                        width: '100%',
                        maxWidth: { xs: '240px', sm: '704px', md: '100%' },
                        paddingX: { xs: spacing[24], sm: spacing[32], md: 0 },
                        marginBottom: { xs: spacing[48], sm: spacing[64], md: spacing[80] },
                        fontWeight: fontWeight.bold,
                        letterSpacing: letterSpacing.negative075,
                        lineHeight: 1.15
                    }}
                >
                    {t('locationsMap.title')}
                </Typography>
                <MapContainer>
                    {/* Desktop & Tablet (≥768px) */}
                    <DesktopTabletMapWrapper>
                        {isOfficesTab && !isGhana && (
                            <HomepageMapOfficesDesktop viewBox={SVG_VIEWBOX.desktop} />
                        )}
                        {isOfficesTab && isGhana && (
                            <HomepageMapOfficesDesktopGhana viewBox={SVG_VIEWBOX.desktop} />
                        )}
                        {isBrokersTab && !isGhana && (
                            <HomepageMapBrokersDesktop viewBox={SVG_VIEWBOX.desktop} />
                        )}
                        {isBrokersTab && isGhana && (
                            <HomepageMapBrokersDesktopGhana viewBox={SVG_VIEWBOX.desktop} />
                        )}
                    </DesktopTabletMapWrapper>

                    {/* Mobile horizontal (501–767px) */}
                    <MobileHorizontalMapWrapper>
                        {isOfficesTab && !isGhana && (
                            <HomepageMapOfficesMobileHorizontal viewBox={SVG_VIEWBOX.mobileHorizontal} />
                        )}
                        {isOfficesTab && isGhana && (
                            <HomepageMapOfficesMobileHorizontalGhana viewBox={SVG_VIEWBOX.mobileHorizontal} />
                        )}
                        {isBrokersTab && !isGhana && (
                            <HomepageMapBrokersMobileHorizontal viewBox={SVG_VIEWBOX.mobileHorizontal} />
                        )}
                        {isBrokersTab && isGhana && (
                            <HomepageMapBrokersMobileHorizontalGhana viewBox={SVG_VIEWBOX.mobileHorizontal} />
                        )}
                    </MobileHorizontalMapWrapper>

                    {/* Mobile vertical (0–500px) */}
                    <MobileVerticalMapWrapper>
                        {isOfficesTab && !isGhana && (
                            <HomepageMapOfficesMobileVertical viewBox={SVG_VIEWBOX.mobileVertical} />
                        )}
                        {isOfficesTab && isGhana && (
                            <HomepageMapOfficesMobileVerticalGhana viewBox={SVG_VIEWBOX.mobileVertical} />
                        )}
                        {isBrokersTab && !isGhana && (
                            <HomepageMapBrokersMobileVertical viewBox={SVG_VIEWBOX.mobileVertical} />
                        )}
                        {isBrokersTab && isGhana && (
                            <HomepageMapBrokersMobileVerticalGhana viewBox={SVG_VIEWBOX.mobileVertical} />
                        )}
                    </MobileVerticalMapWrapper>
                </MapContainer>
                <LocationTabs
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                />
            </Container>

            <Box
                sx={{
                    backgroundColor: color.gray[60],
                    paddingTop: { xs: spacing[32], sm: spacing[48], md: spacing[64] },
                    paddingBottom: { xs: spacing[32], sm: spacing[48], md: spacing[64] },
                }}
            >
                <Container>
                    {activeTab === 'us-offices' &&
                        <TabPanel key={activeTab}>
                            <USOfficeLocations locationsData={usOfficesData} carousel={carousel} />
                        </TabPanel>
                    }
                    {activeTab === 'brokers' &&
                        <TabPanel key={activeTab}>
                            <AuthorizedBrokers
                                brokers={contactsData.brokerContacts.locations}
                                countryGroups={countryGroups}
                                colorsPalette={colorsPalette}
                                twoColumnsFromMd={false}
                                isGhana={isGhana}
                            />
                        </TabPanel>
                    }
                </Container>
            </Box>
        </Box>
    );
}
